import { apiRequest } from "@/lib/api";
import { getDataClient } from "@/lib/dataClient";
import { getSiteName } from "@/lib/siteName";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

export interface BankQuestion {
  id: number;
  sourceType: string;
  sourceName: string;
  sourceYear: number | null;
  examType: string | null;
  gradeLevel: string | null;
  lessonName: string;
  topicName: string;
  subtopic: string | null;
  difficulty: string;
  questionText: string;
  imageUrl?: string;
  imageAlt?: string;
  options: string[];
  correctAnswer: string;
  solutionSummary: string;
  solutionSteps: string[];
  detailedExplanation: string;
  wrongOptionExplanations: Record<string, string> | null;
  hint1: string;
  hint2: string;
  hint3: string;
  qualityScore: number;
  verified: boolean;
}

export interface QuestionBankMeta {
  lessons: string[];
  topics: string[];
  difficulties: string[];
  sourceTypes: string[];
  sourceNames: string[];
  years: string[];
  grades: string[];
  examTypes: string[];
  facets: QuestionFacet[];
}

export interface QuestionFacet {
  lesson: string;
  topic: string;
  difficulty: string;
  sourceType: string;
  sourceName: string;
  year: string;
}

export interface QuestionFilters {
  lesson: string;
  topic: string;
  difficulty: string;
  sourceType: string;
  sourceName: string;
  year: string;
  limit: number;
}

export const DIFFICULTY_LABELS: Record<string, string> = {
  easy: "Kolay",
  medium: "Orta",
  hard: "Zor",
  very_hard: "Çok Zor",
};

export function sourceTypeLabel(type: string, siteName: string): string {
  const labels: Record<string, string> = {
    official: "Çıkmış Soru",
    ai_generated: "AI Soru",
    dersdinle: `${siteName} Sorusu`,
  };
  return labels[type] ?? "Soru";
}

const OPTION_KEYS = ["option_a", "option_b", "option_c", "option_d", "option_e"];

export function mapQuestion(q: Record<string, unknown>): BankQuestion {
  const options = OPTION_KEYS
    .map((k) => q[k])
    .filter((v): v is string => typeof v === "string" && v !== "");
  return {
    id: Number(q.id),
    sourceType: String(q.source_type ?? ""),
    sourceName: String(q.source_name ?? ""),
    sourceYear: q.source_year != null ? Number(q.source_year) : null,
    examType: q.exam_type ? String(q.exam_type) : null,
    gradeLevel: q.grade_level ? String(q.grade_level) : null,
    lessonName: String(q.lesson_name ?? ""),
    topicName: String(q.topic_name ?? ""),
    subtopic: q.subtopic ? String(q.subtopic) : null,
    difficulty: String(q.difficulty ?? ""),
    questionText: String(q.question_text ?? ""),
    imageUrl: String(q.image_url ?? ""),
    imageAlt: String(q.image_alt ?? ""),
    options,
    correctAnswer: String(q.correct_answer ?? ""),
    solutionSummary: String(q.solution_summary ?? ""),
    solutionSteps: Array.isArray(q.solution_steps) ? q.solution_steps.map(String) : [],
    detailedExplanation: String(q.detailed_explanation ?? ""),
    wrongOptionExplanations: (q.wrong_option_explanations ?? null) as Record<string, string> | null,
    hint1: String(q.hint_1 ?? ""),
    hint2: String(q.hint_2 ?? ""),
    hint3: String(q.hint_3 ?? ""),
    qualityScore: Number(q.quality_score ?? 0),
    verified: Boolean(q.verified),
  };
}

function uniqueSorted(values: (string | null | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v !== ""))).sort();
}

/**
 * Filtre seçeneklerini (ders, konu, zorluk, kaynak vb.) soru havuzundan çıkarır.
 * `gradeLevel` verilirse yalnızca o sınıf seviyesine uygun soruların
 * seçeneklerini döndürür (öğrencinin seviyesine uygun ders/konu/yıl listesi).
 */
export function useQuestionBankMeta(gradeLevel: string | null) {
  return useDataQuery<QuestionBankMeta>(async () => {
    const dataClient = getDataClient();
    const columns =
      "lesson_name, topic_name, difficulty, source_type, source_name, source_year, grade_level, exam_type";

    let query = dataClient.from("question_bank").select(columns).eq("verified", true).limit(2000);
    // The authenticated server selects the current learning track, including KPSS.
    const { data, error } = await query;
    if (error) throw error;

    const rows = (data ?? []) as Record<string, unknown>[];
    const facets: QuestionFacet[] = rows.map((r) => ({
      lesson: String(r.lesson_name ?? ""),
      topic: String(r.topic_name ?? ""),
      difficulty: String(r.difficulty ?? ""),
      sourceType: String(r.source_type ?? ""),
      sourceName: String(r.source_name ?? ""),
      year: r.source_year != null ? String(r.source_year) : "",
    }));
    return {
      lessons: uniqueSorted(rows.map((r) => r.lesson_name as string)),
      topics: uniqueSorted(rows.map((r) => r.topic_name as string)),
      difficulties: uniqueSorted(rows.map((r) => r.difficulty as string)),
      sourceTypes: uniqueSorted(rows.map((r) => r.source_type as string)),
      sourceNames: uniqueSorted(rows.map((r) => r.source_name as string)),
      years: uniqueSorted(rows.map((r) => (r.source_year != null ? String(r.source_year) : null))),
      grades: uniqueSorted(rows.map((r) => r.grade_level as string)),
      examTypes: uniqueSorted(rows.map((r) => r.exam_type as string)),
      facets,
    };
  }, [gradeLevel]);
}

/**
 * Soruları sunucu tarafında (dataClient) filtreleyerek getirir.
 * Önce dataClient → sonra AI prensibine uygun, anlık metadata filtreleme.
 * `gradeLevel` verilirse yalnızca öğrencinin sınıf seviyesine uygun
 * sorular getirilir (ör. 5. sınıf öğrencisine TYT/AYT soruları gösterilmez).
 */
export function useQuestionBank(filters: QuestionFilters, gradeLevel: string | null) {
  return useDataQuery<BankQuestion[]>(
    async () => {
      if (filters.limit <= 100) {
        const result = await apiRequest<{questions: Record<string, unknown>[]}>("/learning/session", {
          method: "POST", body: {...filters, count: filters.limit || 20, mode: "practice", request_id: crypto.randomUUID()},
        });
        return result.questions.map(mapQuestion);
      }
      const dataClient = getDataClient();
      // Not: `embedding` (halfvec) sütunu bilinçli olarak seçilmiyor;
      // PostgREST bu özel vektör tipini `select("*")` ile döndürürken sorun çıkarıp boş sonuç üretebiliyor.
      const columns =
        "id, question_code, question_hash, source_type, source_name, source_year, exam_type, grade_level, lesson_name, topic_name, subtopic, question_text, image_url, image_alt, option_a, option_b, option_c, option_d, option_e, correct_answer, difficulty, difficulty_score, solution_summary, solution_steps, detailed_explanation, wrong_option_explanations, hint_1, hint_2, hint_3, ai_generated, verified, quality_score, times_served, times_correct, times_wrong, model_used, created_at, updated_at";
      let query = dataClient
        .from("question_bank")
        .select(columns)
        .eq("verified", true)
        .order("id", { ascending: false })
        .limit(filters.limit || 50);

      // Grade filtering is authoritative on the server, not cached profile metadata.
      if (filters.lesson) query = query.eq("lesson_name", filters.lesson);
      if (filters.topic) query = query.eq("topic_name", filters.topic);
      if (filters.difficulty) query = query.eq("difficulty", filters.difficulty);
      if (filters.sourceType) query = query.eq("source_type", filters.sourceType);
      if (filters.sourceName) query = query.eq("source_name", filters.sourceName);
      if (filters.year) query = query.eq("source_year", Number(filters.year));

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((q) => mapQuestion(q as Record<string, unknown>));
    },
    [
      filters.lesson,
      filters.topic,
      filters.difficulty,
      filters.sourceType,
      filters.sourceName,
      filters.year,
      filters.limit,
      gradeLevel,
    ],
  );
}

/**
 * Öğrencinin cevabını kaydeder (AI hata analizi dahil).
 */
export async function recordAnswer(input: {
  questionId: number;
  answer: string;
  isCorrect: boolean;
  answerTimeSeconds?: number;
  hintUsed?: boolean;
  solutionViewed?: boolean;
}): Promise<{ errorType: string | null } | null> {
  try {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.functions.invoke("question-bank", {
      body: {
        action: "record_answer",
        question_id: input.questionId,
        answer: input.answer,
        is_correct: input.isCorrect,
        answer_time_seconds: input.answerTimeSeconds ?? 0,
        hint_used: input.hintUsed ?? false,
        solution_viewed: input.solutionViewed ?? false,
      },
    });
    if (error) return null;
    return (data ?? null) as { errorType: string | null } | null;
  } catch {
    return null;
  }
}

/**
 * Yanlış yapılan bir soruyu öğrencinin tekrar listesine ekler.
 * Bu kayıt, soru bankası testi, tekil soru çözümü ve deneme sınavı tarafından
 * aynı formatla kullanılır; böylece öğrencinin yanlışları tek yerde toplanır.
 */
export async function recordWrongAnswer(input: {
  lessonName: string;
  topicName: string;
  questionText: string;
  correctAnswer: string;
  answer: string;
}): Promise<boolean> {
  try {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return false;
    const { error } = await dataClient.from("wrong_questions").insert({
      user_id: userId,
      subject: input.lessonName,
      topic: input.topicName,
      question: input.questionText,
      correct_answer: input.correctAnswer,
      my_answer: input.answer,
    });
    return !error;
  } catch {
    return false;
  }
}

/**
 * AI ile yeni sorular üretir (yalnızca havuz yetersiz olduğunda çağrılmalı).
 */
export async function generateQuestions(input: {
  lessonName: string;
  topicName: string;
  difficulty: string;
  count: number;
  gradeLevel: string | null;
}): Promise<{ generated: number; skipped: number } | null> {
  try {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.functions.invoke("question-bank", {
      body: {
        action: "generate_questions",
        lesson_name: input.lessonName,
        topic_name: input.topicName,
        difficulty: input.difficulty,
        count: input.count,
        grade_level: input.gradeLevel ?? "",
        siteName: await getSiteName(),
      },
    });
    if (error) return null;
    return (data ?? null) as { generated: number; skipped: number } | null;
  } catch {
    return null;
  }
}

/**
 * Bir sorunun çözümünü getirir; yoksa AI üretir ve kalıcı olarak kaydeder.
 */
export async function getSolution(input: {
  questionId: number;
  studentLevel?: string;
  force?: boolean;
}): Promise<{ solutionText: string; solutionSteps: string[] } | null> {
  try {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.functions.invoke("question-bank", {
      body: {
        action: "generate_solution",
        question_id: input.questionId,
        student_level: input.studentLevel ?? "",
        force: input.force ?? false,
        siteName: await getSiteName(),
      },
    });
    if (error) return null;
    const sol = data?.solution as { solution_text?: string; solution_steps?: string[] } | undefined;
    return {
      solutionText: sol?.solution_text ?? "",
      solutionSteps: Array.isArray(sol?.solution_steps) ? sol.solution_steps.map(String) : [],
    };
  } catch {
    return null;
  }
}

/**
 * Öğrencinin yetenek skorunu (0-100) okur ve buna göre önerilen zorluğu üretir.
 * Adaptif zorluk: skor düşükse kolay, yüksekse zor soru önerir.
 */
export interface StudentSkill {
  skillScore: number;
  recommendedDifficulty: string;
  recommendedDifficultyLabel: string;
}

export function skillToDifficulty(score: number): string {
  if (score >= 80) return "very_hard";
  if (score >= 60) return "hard";
  if (score >= 35) return "medium";
  return "easy";
}

export function useStudentSkill() {
  return useDataQuery<StudentSkill | null>(async () => {
    const data = await apiRequest<{skill_score:number|null; recommended_difficulty:string}>('/learning/progress');
    if(data.skill_score===null) return null;
    const difficulty=data.recommended_difficulty;
    return {skillScore:data.skill_score,recommendedDifficulty:difficulty,recommendedDifficultyLabel:DIFFICULTY_LABELS[difficulty] ?? difficulty};

  });
}

/**
 * Aralıklı tekrar: tekrar zamanı gelen (yanlış yapılmış, zamanı dolmuş) soruları döndürür.
 */
export interface DueReview {
  questionId: number;
  lessonName: string;
  topicName: string;
  questionText: string;
  correctAnswer: string;
  attemptCount: number;
}

export function useDueReviews() {
  return useDataQuery<DueReview[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("student_question_history")
      .select("question_id, attempt_count, next_review_at")
      .eq("student_id", userId)
      .eq("is_correct", false)
      .lte("next_review_at", new Date().toISOString())
      .order("next_review_at", { ascending: true })
      .limit(20);
    if (error || !data || data.length === 0) return [];

    // Aynı sorunun en güncel denemesini tek kayıt olarak al.
    const latest = new Map<number, number>();
    for (const row of data as { question_id: number; attempt_count: number | null }[]) {
      const qid = Number(row.question_id);
      if (!latest.has(qid)) latest.set(qid, Number(row.attempt_count ?? 1));
    }

    const ids = Array.from(latest.keys());
    const { data: qs, error: qErr } = await dataClient
      .from("question_bank")
      .select("id, lesson_name, topic_name, question_text, correct_answer")
      .in("id", ids);
    if (qErr || !qs) return [];

    return (qs as Record<string, unknown>[]).map((q) => ({
      questionId: Number(q.id),
      lessonName: String(q.lesson_name ?? ""),
      topicName: String(q.topic_name ?? ""),
      questionText: String(q.question_text ?? ""),
      correctAnswer: String(q.correct_answer ?? "").toUpperCase(),
      attemptCount: latest.get(Number(q.id)) ?? 1,
    }));
  });
}
